(() => {
  'use strict';

  const DEFAULT_MAX_PRICE = 50000;
  const OPTION_SETS = {
    gemstone: new Set([
      // Client-approved names.
      'white diamond',
      'yellow diamond',
      'ruby',
      'pink sapphire',
      'blue sapphire',
      'emerald',

      // Existing Search & Discovery values from the Product option: Stone.
      // Shopify returns the exact option labels, so plural values must also
      // be accepted or the theme would hide the entire Gemstone list.
      'white diamonds',
      'yellow diamonds',
      'pink diamond',
      'pink diamonds',
      'blue diamond',
      'blue diamonds',
    ]),
    metal: new Set(['rose gold', 'white gold', 'yellow gold', 'platinum']),
  };

  let scheduled = false;
  const accordionState = new Map();

  const normalizeLabel = (value) =>
    String(value || '')
      .replace(/\([^)]*\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const associatedLabel = (input) => {
    if (input.id) {
      const escapedId = window.CSS?.escape ? CSS.escape(input.id) : input.id.replace(/(["\\])/g, '\\$1');
      const explicitLabel = input.ownerDocument.querySelector(`label[for="${escapedId}"]`);
      if (explicitLabel) return explicitLabel;
    }

    return input.closest('label') || input.closest('.checkbox')?.querySelector('label');
  };

  const optionRow = (input) =>
    input.closest('.facets__inputs-list-item') ||
    input.closest('li') ||
    input.closest('.checkbox');

  const configureHeading = (wrapper) => {
    const desiredHeading = String(wrapper.dataset.jewelryHeading || '').trim();
    if (!desiredHeading) return;

    const heading = wrapper.querySelector('.facets__label');
    if (heading && heading.textContent.trim() !== desiredHeading) {
      heading.textContent = desiredHeading;
    }
  };

  const configureOptionFilter = (wrapper, type) => {
    const allowed = OPTION_SETS[type];
    if (!allowed) return;

    configureHeading(wrapper);

    wrapper.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const label = associatedLabel(input);
      const normalized = normalizeLabel(label?.textContent || input.value);
      const shouldShow = allowed.has(normalized) || input.checked;
      const row = optionRow(input);

      if (row) {
        row.hidden = !shouldShow;
        row.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      }
    });

    // The approved lists contain no more than six entries, so the theme's
    // show-more control is unnecessary after the unwanted values are hidden.
    wrapper.querySelectorAll('.show-more').forEach((element) => {
      element.hidden = true;
    });
  };

  const numberFromText = (value) => {
    const cleaned = String(value || '').replace(/[^0-9.-]/g, '');
    const number = Number(cleaned);
    return Number.isFinite(number) ? number : null;
  };

  const formatMaximumLike = (original, maximum) => {
    const decimalMatch = String(original).match(/([.,])(\d{2})\s*$/);
    const decimals = decimalMatch ? decimalMatch[2] : '';
    const formatted = maximum.toLocaleString('en-US');
    return decimals ? `${formatted}.${decimals}` : formatted;
  };

  const replaceLargeCurrencyAmount = (element, maximum) => {
    if (element.children.length > 0) return;

    const original = element.textContent;
    const matches = [...original.matchAll(/\d[\d,.]*/g)];
    if (!matches.length) return;

    let updated = original;
    let changed = false;

    for (const match of matches.reverse()) {
      const value = numberFromText(match[0]);
      if (value !== null && value > maximum) {
        const replacement = formatMaximumLike(match[0], maximum);
        updated = `${updated.slice(0, match.index)}${replacement}${updated.slice(match.index + match[0].length)}`;
        changed = true;
      }
    }

    if (changed) element.textContent = updated;
  };

  const configurePriceFilter = (wrapper) => {
    const maximum = Number(wrapper.dataset.jewelryPriceMax) || DEFAULT_MAX_PRICE;

    wrapper.querySelectorAll('input').forEach((input) => {
      const name = input.name || '';
      const isPriceInput = name.includes('filter.v.price') || input.type === 'range';
      if (!isPriceInput) return;

      const isUpperBound = name.endsWith('.lte') || input.dataset.priceRole === 'max';
      const isLowerBound = name.endsWith('.gte') || input.dataset.priceRole === 'min';

      if (input.type === 'range' || isUpperBound || isLowerBound) {
        input.max = String(maximum);
        input.dataset.max = String(maximum);
      }

      if (isUpperBound) {
        input.placeholder = maximum.toLocaleString('en-US');
      }

      const currentValue = numberFromText(input.value);
      if (currentValue !== null && currentValue > maximum) {
        input.value = String(maximum);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    wrapper.querySelectorAll('[data-range-max]').forEach((element) => {
      element.dataset.rangeMax = maximum.toLocaleString('en-US');
    });

    // Supports the existing custom slider labels without depending on one
    // exact class name. Only values inside the price filter and over the cap
    // are changed.
    wrapper.querySelectorAll('*').forEach((element) => {
      replaceLargeCurrencyAmount(element, maximum);
    });
  };

  const accordionKey = (panel) => {
    const directKey = panel.dataset.jfdAccordionKey;
    if (directKey) return directKey;

    const keyedWrapper = panel.closest('[data-jfd-accordion-key]');
    if (keyedWrapper?.dataset.jfdAccordionKey) {
      return keyedWrapper.dataset.jfdAccordionKey;
    }

    if (panel.classList.contains('jewelry-sort-filter__panel')) return 'sort';
    if (panel.closest('.jewelry-price-filter')) return 'filter.v.price';
    if (panel.closest('.jewelry-filter--availability')) return 'filter.v.availability';
    if (panel.closest('.jewelry-filter--brand')) return 'filter.p.vendor';
    if (panel.closest('.jewelry-filter--gemstone')) return 'gemstone';
    if (panel.closest('.jewelry-filter--metal')) return 'metal';

    return panel.id || null;
  };

  const panelHasActiveValue = (panel) => {
    const checkedOption = panel.querySelector(
      'input[type="checkbox"]:checked, input[type="radio"]:checked'
    );
    const populatedPriceInput = [...panel.querySelectorAll('input[name*="filter.v.price"]')].some(
      (input) => String(input.value || '').trim() !== ''
    );

    return Boolean(checkedOption || populatedPriceInput);
  };

  const rememberAccordionState = (panel, key) => {
    if (!key || panel.dataset.jfdAccordionStateBound === 'true') return;

    panel.dataset.jfdAccordionStateBound = 'true';
    panel.addEventListener('toggle', () => {
      accordionState.set(key, panel.open);
    });
  };

  const configureAccordionDefaults = (root = document) => {
    root.querySelectorAll('.jewelry-filter-drawer').forEach((drawer) => {
      const panels = drawer.querySelectorAll(
        '.jewelry-sort-filter__panel, .jewelry-filter .facets__panel, .jewelry-price-filter .facets__panel'
      );

      panels.forEach((panel) => {
        const key = accordionKey(panel);
        rememberAccordionState(panel, key);

        const rememberedOpenState = key ? accordionState.get(key) : undefined;
        const defaultOpenState = panel.classList.contains('jewelry-sort-filter__panel')
          ? true
          : panelHasActiveValue(panel);
        const shouldBeOpen = rememberedOpenState === undefined
          ? defaultOpenState
          : rememberedOpenState;

        if (panel.open !== shouldBeOpen) {
          panel.open = shouldBeOpen;
        }
      });
    });
  };

  const configure = (root = document) => {
    root.querySelectorAll('[data-jewelry-heading]').forEach(configureHeading);

    root.querySelectorAll('.jewelry-filter--gemstone').forEach((wrapper) => {
      configureOptionFilter(wrapper, 'gemstone');
    });

    root.querySelectorAll('.jewelry-filter--metal').forEach((wrapper) => {
      configureOptionFilter(wrapper, 'metal');
    });

    root.querySelectorAll('.jewelry-price-filter').forEach(configurePriceFilter);
    configureAccordionDefaults(root);
  };

  const scheduleConfigure = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      configure(document);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleConfigure, { once: true });
  } else {
    scheduleConfigure();
  }

  document.addEventListener('shopify:section:load', scheduleConfigure);
  window.addEventListener('pageshow', scheduleConfigure);

  new MutationObserver(scheduleConfigure).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
